/* eslint-disable jsx-a11y/heading-has-content */

import React from 'react'

/**
 * The `Grid2md4` component provides a reusable wrapper for grid layouts with flexible column configurations.
 *
 * - It uses Tailwind CSS classes to define a responsive grid:
 *   - `grid`: Enables CSS grid layout.
 *   - `grid-cols-2`: Sets the grid to have two columns by default.
 *   - `md:grid-cols-4`: Adjusts the grid to have four columns on medium screens and larger.
 *   - `gap-4`: Adds consistent spacing (gap) between grid items.
 * - The `className` prop allows additional styles to be merged with the default styles, enabling customization.
 * - The `props` spread operator (`{...props}`) allows passing through any valid props (e.g., `id`, `style`, `onClick`).
 *
 * This component is ideal for layouts that require a grid structure with adaptable column counts,
 * such as galleries, product listings, or content sections with uniform spacing.
 */
export const Grid2md4 = (props) => (
  <div
    {...props}
    className={'grid grid-cols-2 md:grid-cols-4 gap-4 ' + props.className}
  />
)

export const Grid1md2 = (props) => (
  <div
    {...props}
    className={
      'grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 ' + props.className
    }
  />
)

/**
 * The `Title` component is a reusable wrapper for rendering text elements with consistent typography styles.
 *
 * - It applies Tailwind CSS classes to define font size and spacing:
 *   - `text-2xl`, `md:text-3xl`, `lg:text-4xl`: Responsive font sizes for different screen widths.
 *   - `max-w-2xl`: Limits the maximum width of the text block for better readability.
 *   - `leading-tight`: Adjusts line height for compact and clean text presentation.
 * - The `className` prop allows additional styles to be merged with the default styles, providing flexibility.
 * - The `props` spread operator (`{...props}`) allows passing through any valid props (e.g., `id`, `style`, `onClick`).
 *
 * This component is ideal for titles, headers, or any prominent text element in your app,
 * ensuring consistent styling while remaining adaptable to specific use cases.
 */
export const Title = (props) => (
  <div
    {...props}
    className={
      'text-2xl md:text-3xl lg:text-4xl max-w-2xl leading-tight ' +
      props.className
    }
  />
)

export const H2 = (props) => (
  <h2
    {...props}
    className={'text-3xl font-bold leading-tight ' + props.className}
  />
)

export const H3 = (props) => (
  <h3
    {...props}
    className={'text-2xl font-bold mb-1 leading-tight ' + props.className}
  />
)

export const H3Like = (props) => (
  <div
    {...props}
    className={'text-2xl font-bold mb-1 leading-tight ' + props.className}
  />
)

export const CardLink = ({
  target = '_blank',
  rel = 'noopener noreferrer',
  ...props
}) => (
  // eslint-disable-next-line jsx-a11y/anchor-has-content
  <a
    {...props}
    className='linkable'
    target={target}
    rel={target === '_blank' ? rel : undefined}
  />
)

export const DividerSection = (props) => (
  <section
    className='text-white bg-dark pt-8 pb-16'
    id={props.id || ''}
    style={props.style}
  >
    <div className='xl:container mx-auto px-4'>{props.children}</div>
  </section>
)

export const DividerLabel = (props) => (
  <div className='text-white mt-3 hoverable' {...props} />
)

/**
 * The `MainSection` component provides a reusable wrapper for main content sections.
 *
 * - It ensures consistent layout and spacing using Tailwind CSS classes:
 *   - `xl:container`: Limits the width on larger screens.
 *   - `mx-auto`: Centers the section horizontally.
 *   - `px-4`: Adds horizontal padding for better readability.
 * - The optional `id` prop allows sections to be identified for navigation or anchor links.
 * - The `children` prop lets you nest any content inside this component,
 *   wrapped with vertical padding (`pt-8 pb-16`) to create visual separation.
 *
 * This component is ideal for organizing content into distinct, structured sections
 * while maintaining a consistent visual hierarchy across your app.
 */
export const MainSection = (props) => (
  <section className='xl:container mx-auto px-4' id={props.id || ''}>
    <div className='pt-8 pb-16'>{props.children}</div>
  </section>
)

export const MainLabel = (props) => (
  <div className='hoverable mt-4' {...props} />
)

export const Card = (props) => (
  <div
    {...props}
    className='relative shadow-card rounded-2xl overflow-hidden'
  />
)

export const SquareSpacer = (props) => (
  <div className='bg-white rounded-2xl pb-full' {...props} />
)
